import { useCallback, useEffect, useState } from "react"
import type { Editor } from "@tiptap/react"
import { Lightbulb } from "lucide-react"

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"

// --- Lib ---
import { NOTION_BLOCK_SHORTCUTS } from "@/lib/notion-shortcuts"
import { isNodeInSchema, isNodeTypeSelected } from "@/lib/tiptap-utils"

export interface UseCalloutConfig {
  editor?: Editor | null
  /** 使えないときにボタンを隠すか */
  hideWhenUnavailable?: boolean
  onToggled?: () => void
}

export function canToggleCallout(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false
  if (!isNodeInSchema("callout", editor)) return false
  if (isNodeTypeSelected(editor, ["image"])) return false
  return editor.can().toggleCallout()
}

export function toggleCallout(editor: Editor | null): boolean {
  if (!editor || !canToggleCallout(editor)) return false
  return editor.chain().focus().toggleCallout().run()
}

export function useCallout(config?: UseCalloutConfig) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onToggled,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canToggle = canToggleCallout(editor)
  const isActive = editor?.isActive("callout") || false

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(!hideWhenUnavailable || canToggleCallout(editor))
    }

    handleSelectionUpdate()
    editor.on("selectionUpdate", handleSelectionUpdate)
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    }
  }, [editor, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    const success = toggleCallout(editor)
    if (success) onToggled?.()
    return success
  }, [editor, onToggled])

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle,
    label: "Callout",
    shortcutKeys: NOTION_BLOCK_SHORTCUTS.callout.keys,
    Icon: Lightbulb,
  }
}
